import { Card } from "@/components/ui/card";
import LoginForm from "./LoginForm";
import { useTheme } from "@/context/ThemeContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { UserType } from "@/types/user";
import { authLabels, cookieConsentLabels, footerLabels } from "@/i18n";
import { useLanguage } from "@/context/LanguageContext";
import { useMemo, useState } from "react";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { openBileniaWebApp } from "@/lib/native/openBileniaWeb";
import PrivacyPolicyDialog, {
  type PrivacyPolicyTab,
} from "@/components/footer/privacy_policy_dialog";
import { openCookieConsentSettings } from "@/lib/cookieConsent";

interface AuthCardProps {
  isDialog?: boolean;
  defaultUserType?: UserType;
  /** Tighter padding for Capacitor /auth */
  compact?: boolean;
}

const AuthCard = ({
  isDialog = false,
  defaultUserType,
  compact: compactProp,
}: AuthCardProps) => {
  const { isNative } = useNativePlatform();
  const compact = compactProp ?? (isNative && !isDialog);
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const isMobile = useIsMobile();
  const [policyOpen, setPolicyOpen] = useState(false);
  const [policyTab, setPolicyTab] = useState<PrivacyPolicyTab>("privacy");

  const { language: lang } = useLanguage();
  const labels = useMemo(() => authLabels(lang), [lang]);
  const footer = useMemo(() => footerLabels(lang), [lang]);
  const consentT = useMemo(() => cookieConsentLabels(lang), [lang]);

  return (
    <Card
      className={`w-full ${
        isDialog
          ? "max-w-full"
          : compact
            ? "native-auth-card max-w-full mx-0 p-3 space-y-2 border-0 shadow-none rounded-xl"
            : isMobile
              ? "max-w-full mx-2"
              : "max-w-md"
      } ${compact ? "" : "p-4 md:p-8 space-y-4 md:space-y-6"} ${
        isDark
          ? "bg-black/50 backdrop-blur-sm border border-white/10"
          : "bg-white/95 backdrop-blur-sm border border-gray-200 shadow-md"
      }`}
    >
      <div className="text-center">
        <h2
          className={`${
            compact ? "text-xl leading-tight" : isMobile ? "text-2xl" : "text-3xl"
          } font-bold ${isDark ? "text-white" : "text-gray-800"}`}
        >
          {labels.buttons?.login}
        </h2>
      </div>
      <div className="overflow-x-auto">
        <LoginForm isDialog={isDialog} defaultUserType={defaultUserType} />
      </div>

      {compact && isNative && !isDialog ? (
        <div className="pt-2 pb-1 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 shrink-0">
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => void openBileniaWebApp("/contact")}
          >
            {labels.nativeAuthLinks?.contact ?? "Kontakt"}
          </button>
          <span className="text-xs text-muted-foreground/60">•</span>
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => {
              setPolicyTab("privacy");
              setPolicyOpen(true);
            }}
          >
            {labels.nativeAuthLinks?.privacy ?? "Integritetspolicy"}
          </button>
          <span className="text-xs text-muted-foreground/60">•</span>
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => {
              setPolicyTab("cookies");
              setPolicyOpen(true);
            }}
          >
            {footer.privacyDialog?.cookiesTab ?? "Cookiepolicy"}
          </button>
          <span className="text-xs text-muted-foreground/60">•</span>
          <button
            type="button"
            className="text-xs text-primary hover:underline transition-colors"
            onClick={() => openCookieConsentSettings({ edit: true })}
          >
            {consentT.changeSettings}
          </button>
        </div>
      ) : null}

      <PrivacyPolicyDialog
        open={policyOpen}
        onOpenChange={setPolicyOpen}
        initialTab={policyTab}
      />
    </Card>
  );
};

export default AuthCard;
